import { useState } from 'react';
import styled from 'styled-components';
import { validateFileType, validateFileSize } from '../../../services/storageService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #6c757d;
`;

const UploadArea = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #007bff;
    background-color: #e7f3ff;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      border-color: #dee2e6;
      background-color: #f8f9fa;
    }
  }
`;

const UploadIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
  color: #6c757d;
`;

const UploadText = styled.div`
  font-size: 14px;
  color: #6c757d;
  text-align: center;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  position: relative;
`;

const FilePreview = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoIcon = styled.div`
  font-size: 24px;
  color: #6c757d;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #007bff;
  transition: width 0.3s;
  width: ${props => props.progress}%;
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #c82333;
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const FileUploader = ({ images = [], videos = [], onImagesChange, onVideosChange }) => {
  const [uploadingImages, setUploadingImages] = useState({});
  const [uploadingVideos, setUploadingVideos] = useState({});

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (!validateFileType(file, 'image')) {
        errors.push(`${file.name}: 이미지 파일만 업로드 가능합니다.`);
        return;
      }
      if (images.length + validFiles.length >= 5) {
        errors.push('이미지는 최대 5개까지 업로드 가능합니다.');
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      onImagesChange([...images, ...validFiles]);
    }

    e.target.value = '';
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (!validateFileType(file, 'video')) {
        errors.push(`${file.name}: 동영상 파일만 업로드 가능합니다.`);
        return;
      }
      if (!validateFileSize(file, 50)) {
        errors.push(`${file.name}: 동영상은 50MB 이하만 업로드 가능합니다.`);
        return;
      }
      if (videos.length + validFiles.length >= 3) {
        errors.push('동영상은 최대 3개까지 업로드 가능합니다.');
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      onVideosChange([...videos, ...validFiles]);
    }

    e.target.value = '';
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const removeVideo = (index) => {
    const newVideos = videos.filter((_, i) => i !== index);
    onVideosChange(newVideos);
  };

  const getImagePreviewUrl = (file) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return file.url;
  };

  return (
    <Container>
      <Section>
        <SectionTitle>
          참고 이미지
          <FileCount>({images.length}/5)</FileCount>
        </SectionTitle>

        <UploadArea htmlFor="image-upload" className={images.length >= 5 ? 'disabled' : ''}>
          <UploadIcon>+</UploadIcon>
          <UploadText>
            클릭하여 이미지 추가<br />
            <small>(최대 5개)</small>
          </UploadText>
          <HiddenInput
            id="image-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            disabled={images.length >= 5}
          />
        </UploadArea>

        {images.length > 0 && (
          <FileList>
            {images.map((file, index) => (
              <FileItem key={index}>
                <FilePreview>
                  <PreviewImage src={getImagePreviewUrl(file)} alt={file.name} />
                </FilePreview>
                <FileInfo>
                  <FileName>{file.name}</FileName>
                  <FileSize>{formatFileSize(file.size)}</FileSize>
                  {uploadingImages[index] !== undefined && (
                    <ProgressBar>
                      <ProgressFill progress={uploadingImages[index]} />
                    </ProgressBar>
                  )}
                </FileInfo>
                <RemoveButton onClick={() => removeImage(index)}>삭제</RemoveButton>
              </FileItem>
            ))}
          </FileList>
        )}
      </Section>

      <Section>
        <SectionTitle>
          참고 동영상
          <FileCount>({videos.length}/3)</FileCount>
        </SectionTitle>

        <UploadArea htmlFor="video-upload" className={videos.length >= 3 ? 'disabled' : ''}>
          <UploadIcon>+</UploadIcon>
          <UploadText>
            클릭하여 동영상 추가<br />
            <small>(최대 3개, 각 50MB 이하)</small>
          </UploadText>
          <HiddenInput
            id="video-upload"
            type="file"
            accept="video/*"
            multiple
            onChange={handleVideoSelect}
            disabled={videos.length >= 3}
          />
        </UploadArea>

        {videos.length > 0 && (
          <FileList>
            {videos.map((file, index) => (
              <FileItem key={index}>
                <FilePreview>
                  <VideoIcon>▶</VideoIcon>
                </FilePreview>
                <FileInfo>
                  <FileName>{file.name}</FileName>
                  <FileSize>{formatFileSize(file.size)}</FileSize>
                  {uploadingVideos[index] !== undefined && (
                    <ProgressBar>
                      <ProgressFill progress={uploadingVideos[index]} />
                    </ProgressBar>
                  )}
                </FileInfo>
                <RemoveButton onClick={() => removeVideo(index)}>삭제</RemoveButton>
              </FileItem>
            ))}
          </FileList>
        )}
      </Section>
    </Container>
  );
};
